import { z } from "zod";
import { getIdSchema, ID, makeIdGenerator } from "../utils";
import { accountIdSchema } from "./accountSchema";
import { hiveIdSchema } from "./hiveSchema";
import { loadIdSchema } from "./loadSchema";

export const eventIdPrefix = "evt_";
export type EventId = ID<typeof eventIdPrefix>;
export const generateEventId = makeIdGenerator(eventIdPrefix);
export const eventIdSchema = getIdSchema(eventIdPrefix, "EventId");

export enum EventType {
  note = "note",
  move = "move",
  calibration = "calibration",
  harvest = "harvest",
  super_added = "super_added",
  flow_change = "flow_change",
  hive_divergence = "hive_divergence",
  load_flow_change = "load_flow_change",
  battery_level_change = "battery_level_change",
  connectivity_change = "connectivity_change",
}

/**
 * Event types whose *records* the detection machine owns: reconciled
 * (updated in place / soft-deleted as the data dictates), rejected by the
 * event write endpoints, and hidden from the event timeline until the
 * briefing page ships. The flow types still drive the flow-status displays
 * (sub-header badges, table/card icons, chart bands).
 *
 * Note this is about *management*, not detection: harvest and super_added
 * are also machine-detected, but they describe the beekeeper's physical
 * actions, so the machine only suggests them (create-once persistence) and
 * the beekeeper edits, retitles, and deletes them like any logged event.
 */
export const SYSTEM_MANAGED_EVENT_TYPES = [
  EventType.flow_change,
  EventType.hive_divergence,
  EventType.load_flow_change,
  EventType.battery_level_change,
  EventType.connectivity_change,
] as const;

export type SystemManagedEventType =
  (typeof SYSTEM_MANAGED_EVENT_TYPES)[number];

export function isSystemManagedEventType(
  type: EventType,
): type is SystemManagedEventType {
  return (SYSTEM_MANAGED_EVENT_TYPES as readonly EventType[]).includes(type);
}

// Strictness lives on each structured `details`, not the top-level row.
// `details` is the polymorphic part — its shape varies per `type` — so
// rejecting unknown keys there is what catches a misshapen payload (e.g. a
// `move` carrying harvest fields). The top-level row is fixed and every field
// is validated explicitly, so it stays loose: unknown columns (e.g. the
// `deletedAt` soft-delete marker — ADR-0002 Addendum A.4) are stripped on read
// instead of tripping a strict parse and 500ing the API response that
// serializes through this schema.
//
// `note` has no payload, so its (base) details is the empty object, left loose
// — there is nothing to be strict about.
const baseEventSchema = z.object({
  id: eventIdSchema,
  accountId: accountIdSchema,
  loadId: loadIdSchema.nullable(),
  hiveId: hiveIdSchema.nullable(),
  type: z.enum(Object.values(EventType)),
  start: z.coerce.date(),
  title: z.string(),
  description: z.string().default(""),
  details: z.object({}),
});

export const noteEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.note),
});

export const moveEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.move),
  details: z
    .object({
      movedTo: z
        .string()
        .transform((val) => val.trim())
        .refine((val) => val.length > 0, {
          message: "Please specify a location",
        }),
    })
    .strict(),
});

export const referencePointSchema = z.object({
  rawWeight: z.number(),
  referenceWeight: z.number(),
});

export const calibrationEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.calibration),
  details: z
    .object({
      referencePoints: z.tuple([referencePointSchema, referencePointSchema]),
    })
    .strict(),
});

export const harvestScaleDataSchema = z.object({
  weightBefore: z.number().nullable().optional(),
  weightAfter: z.number().nullable().optional(),
  kgDropped: z.number().nullable().optional(),
});

export const harvestEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.harvest),
  // Keys are hiveIds; each entry holds the weight data for that hive.
  details: z.record(z.string(), harvestScaleDataSchema),
});

export const superAddedScaleDataSchema = z.object({
  weightBefore: z.number().nullable().optional(),
  weightAfter: z.number().nullable().optional(),
  kgAdded: z.number().nullable().optional(),
});

export const superAddedEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.super_added),
  // Keys are hiveIds; each entry holds the weight data for that hive.
  details: z.record(z.string(), superAddedScaleDataSchema),
});

// Detected events store only the state transition — no gains, means, or
// z-scores. Magnitudes are point-in-time projections of the readings and are
// reconstructable on demand; storing them would freeze them at an ambiguous
// timescale (ADR-0002 §7.2).
export const flowEventStateSchema = z.enum(["on_flow", "typical", "declining"]);

export const flowChangeEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.flow_change),
  details: z
    .object({
      fromState: flowEventStateSchema,
      toState: flowEventStateSchema,
    })
    .strict(),
});

// Load-level flow: the yard's overall verdict (ADR-0002 Addendum A), subject =
// the load (hiveId null). Same state vocabulary and details shape as per-hive
// flow_change — a distinct type so it owns its reconcile namespace and the
// consumers (Loads filter, alerting) query it without a hiveId discriminator.
export const loadFlowChangeEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.load_flow_change),
  details: z
    .object({
      fromState: flowEventStateSchema,
      toState: flowEventStateSchema,
    })
    .strict(),
});

export const batteryEventStateSchema = z.enum(["ok", "low"]);

// Hive-subject battery state transition (ADR-0002 Addendum B): `low` on a
// reading below LOW_BATTERY_THRESHOLD, back to `ok` on a reading above
// BATTERY_RECOVERED_THRESHOLD. `voltage` is the reading that crossed the
// threshold — a fact of the transition itself (like a harvest's weights,
// ADR-0002 §14), not a rolling statistic, so storing it doesn't breach the
// §7.2 no-magnitudes rule; it lets the briefing/alert say "battery at 3.4 V"
// without refetching readings.
export const batteryLevelChangeEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.battery_level_change),
  details: z
    .object({
      fromState: batteryEventStateSchema,
      toState: batteryEventStateSchema,
      voltage: z.number(),
    })
    .strict(),
});

export const connectivityEventStateSchema = z.enum(["online", "offline"]);

// Hive-subject connectivity transition, emitted by the hive-cursor
// connectivity tick (ADR-0003), not the detection machine — an offline
// hive's silence never advances the machine's data cursor, so absence-based
// events can't come from a forward detector. Both edges are dated from the
// hive's actual contact evidence — its own scale_report times for WHS
// scales (they don't buffer, so the report record is the transmission
// record), its devices' device_submission times for fatbees (they buffer,
// so only their sends prove contact) — never tick or sync-ingest time.
// `offline` is dated at the last contact, `online` at the first contact
// after the outage.
export const connectivityChangeEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.connectivity_change),
  details: z
    .object({
      fromState: connectivityEventStateSchema,
      toState: connectivityEventStateSchema,
    })
    .strict(),
});

export const divergenceStateSchema = z.enum(["behind", "typical", "ahead"]);

export const hiveDivergenceEventSchema = baseEventSchema.extend({
  type: z.literal(EventType.hive_divergence),
  details: z
    .object({
      fromState: divergenceStateSchema,
      toState: divergenceStateSchema,
    })
    .strict(),
});

export const eventSchema = z
  .discriminatedUnion("type", [
    noteEventSchema,
    moveEventSchema,
    calibrationEventSchema,
    harvestEventSchema,
    superAddedEventSchema,
    flowChangeEventSchema,
    hiveDivergenceEventSchema,
    loadFlowChangeEventSchema,
    batteryLevelChangeEventSchema,
    connectivityChangeEventSchema,
  ])
  .meta({ id: "Event" });

// Write body for PUT /api/events/:eventId. Differs from `eventSchema` in two
// ways, each pushing a handler concern into the type:
//   1. Omits `accountId` — the account a row belongs to is not the client's to
//      choose, so the server stamps `req.user.accountId` on write (matching
//      putLoadSchema, putHiveSchema, …). Dropping the field is what makes the
//      cross-tenant injection unrepresentable rather than merely rejected.
//   2. Includes only the beekeeper-authorable types (the complement of
//      SYSTEM_MANAGED_EVENT_TYPES). Detected events are produced solely by the
//      detection machine (ADR-0002); leaving them out means a detected-type
//      body is rejected at the schema boundary, so the handler needs no
//      body-type guard.
export const putEventSchema = z
  .discriminatedUnion("type", [
    noteEventSchema.omit({ accountId: true }),
    moveEventSchema.omit({ accountId: true }),
    calibrationEventSchema.omit({ accountId: true }),
    harvestEventSchema.omit({ accountId: true }),
    superAddedEventSchema.omit({ accountId: true }),
  ])
  .meta({ id: "EventUpdate" });

export type Event = z.infer<typeof eventSchema>;
export type PutEvent = z.infer<typeof putEventSchema>;
export type NoteEvent = z.infer<typeof noteEventSchema>;
export type MoveEvent = z.infer<typeof moveEventSchema>;
export type CalibrationEvent = z.infer<typeof calibrationEventSchema>;
export type ReferencePoint = z.infer<typeof referencePointSchema>;
export type HarvestEvent = z.infer<typeof harvestEventSchema>;
export type HarvestScaleData = z.infer<typeof harvestScaleDataSchema>;
export type SuperAddedEvent = z.infer<typeof superAddedEventSchema>;
export type SuperAddedScaleData = z.infer<typeof superAddedScaleDataSchema>;
export type FlowEventState = z.infer<typeof flowEventStateSchema>;
export type FlowChangeEvent = z.infer<typeof flowChangeEventSchema>;
export type LoadFlowChangeEvent = z.infer<typeof loadFlowChangeEventSchema>;
export type DivergenceState = z.infer<typeof divergenceStateSchema>;
export type HiveDivergenceEvent = z.infer<typeof hiveDivergenceEventSchema>;
export type BatteryEventState = z.infer<typeof batteryEventStateSchema>;
export type BatteryLevelChangeEvent = z.infer<
  typeof batteryLevelChangeEventSchema
>;
export type ConnectivityEventState = z.infer<
  typeof connectivityEventStateSchema
>;
export type ConnectivityChangeEvent = z.infer<
  typeof connectivityChangeEventSchema
>;
